import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, Eye } from "lucide-react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Section, FlowPicker, ModuleChecklist, TabOrder, Wording, Categories, History } from "./BuilderParts";
import { VerticalPreview } from "./VerticalPreview";
import { BLANK, STATUS, deriveTabs } from "./vbUtils";

function Basics({ r, meta, set, isNew, shops }) {
  const sel = "border border-input bg-background h-9 px-2 text-sm w-full";
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-xs text-muted-foreground">Name<Input className="rounded-none mt-0.5" value={r.label} onChange={(e) => set({ label: e.target.value })} data-testid="vb-name" /></label>
      <label className="text-xs text-muted-foreground">Icon
        <select className={`${sel} mt-0.5`} value={r.icon} onChange={(e) => set({ icon: e.target.value })} data-testid="vb-icon">{meta.icons.map((i) => <option key={i} value={i}>{i}</option>)}</select>
      </label>
      <label className="text-xs text-muted-foreground sm:col-span-2">Short blurb (shown on the wall)<Input className="rounded-none mt-0.5" value={r.blurb} onChange={(e) => set({ blurb: e.target.value })} data-testid="vb-blurb" /></label>
      <label className="text-xs text-muted-foreground">Product
        <select className={`${sel} mt-0.5`} value={r.product} disabled={!isNew} onChange={(e) => set({ product: e.target.value, flow: e.target.value === "morrconnect" ? "call" : "order" })} data-testid="vb-product">
          <option value="morrcart">MorrCart</option><option value="morrconnect">MorrConnect</option>
        </select>
      </label>
      <label className="text-xs text-muted-foreground">Price tier note (display only)<Input className="rounded-none mt-0.5" value={r.price_note} onChange={(e) => set({ price_note: e.target.value })} data-testid="vb-price-note" /></label>
      {!isNew && (
        <label className="text-xs text-muted-foreground">Status
          <select className={`${sel} mt-0.5`} value={r.status} onChange={(e) => set({ status: e.target.value })} data-testid="vb-status">
            {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k} disabled={shops > 0 && k !== "live"}>{l}</option>)}
          </select>
          {shops > 0 && <span className="block mt-1">{shops} shop{shops !== 1 ? "s" : ""} use this, so it stays Live.</span>}
        </label>
      )}
    </div>
  );
}

function AiDraft({ r, set }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await opApi.post("/admin/verticals-ai-draft", { description: r.description, product: r.product });
      const { status, product, ...rest } = data;
      set(rest);
      toast.success("Draft filled in. Review everything, then save.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Textarea className="rounded-none text-sm" rows={3} placeholder="e.g. General contractor: bids, scheduled jobs, crews, change orders"
        value={r.description} onChange={(e) => set({ description: e.target.value })} data-testid="vb-description" />
      <Button type="button" variant="outline" className="rounded-none gap-1.5" disabled={busy || (r.description || "").trim().length < 10} onClick={run} data-testid="vb-ai-draft">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Drafting… (about 20 seconds)" : "Draft with AI"}
      </Button>
    </div>
  );
}

export function VerticalEditor({ vkey, meta, onClose, onPreview }) {
  const [r, setR] = useState(vkey ? null : { ...BLANK });
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const load = useCallback(() => vkey && opApi.get(`/admin/verticals/${vkey}`).then(({ data }) => { setR(data); setOrig(data); }).catch((e) => toast.error(apiError(e.response?.data?.detail))), [vkey]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const set = (patch) => setR((x) => ({ ...x, ...patch }));
  const tabs = deriveTabs(r, meta.modules, meta.flows);
  const shops = orig?.shops || 0;
  const flowLocked = shops > 0;

  const save = async () => {
    setConfirm(false); setSaving(true);
    const body = { ...r, tabs };
    try {
      const { data } = vkey ? await opApi.put(`/admin/verticals/${vkey}`, body) : await opApi.post("/admin/verticals", body);
      toast.success(vkey ? "Saved" : "Draft created");
      if (!vkey) return onClose(data.key);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const restore = async (h) => {
    setSaving(true);
    try { await opApi.post(`/admin/verticals/${vkey}/restore/${h.id}`); toast.success("Version restored"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="vertical-editor">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button variant="ghost" className="rounded-none gap-1.5 px-2" onClick={() => onClose()} data-testid="vb-back"><ArrowLeft className="h-4 w-4" /> All verticals</Button>
        <h2 className="font-display font-bold text-xl flex-1">{vkey ? `Edit ${orig?.label}` : "New vertical"}</h2>
        {vkey && <Button variant="outline" className="rounded-none gap-1.5" onClick={() => onPreview(vkey)} data-testid="vb-open-preview"><Eye className="h-4 w-4" /> Open preview</Button>}
        <Button className="rounded-none" disabled={saving} onClick={() => (shops > 0 ? setConfirm(true) : save())} data-testid="vb-save">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{vkey ? "Save" : "Save draft"}
        </Button>
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="space-y-4">
          <Section title="Basics" testId="vb-sec-basics"><Basics r={r} meta={meta} set={set} isNew={!vkey} shops={shops} /></Section>
          <Section title="Describe and draft with AI" help="Describe the business in plain words. AI fills in the wording, categories and modules below. Nothing is saved until you click Save." testId="vb-sec-ai"><AiDraft r={r} set={set} /></Section>
          <Section title="Main flow" help={flowLocked ? `${shops} shop${shops !== 1 ? "s use" : " uses"} this vertical, so the main flow is locked. Duplicate it to try a different flow.` : "How customers start on the public page. This also picks the storefront type."} testId="vb-sec-flow">
            <FlowPicker r={r} flows={meta.flows} locked={flowLocked} set={set} />
          </Section>
          <Section title="Modules" help="Tick what this vertical includes. Locked ones are needed by the main flow. Turning a module off only hides its tab; no shop data is deleted." testId="vb-sec-modules">
            <ModuleChecklist r={r} modules={meta.modules} flows={meta.flows} set={set} />
          </Section>
          <Section title="Tab order" help="Drag to reorder the owner's dashboard tabs." testId="vb-sec-tabs"><TabOrder r={r} tabs={tabs} set={set} /></Section>
          <Section title="Custom wording" help="Leave a box empty to keep the standard word." testId="vb-sec-wording"><Wording r={r} tabs={tabs} lexKeys={meta.lexicon_keys} set={set} /></Section>
          <Section title={r.product === "morrconnect" ? "Specialties" : "Request categories"} help="What customers pick from when they ask for something." testId="vb-sec-categories"><Categories r={r} set={set} /></Section>
          {vkey && <Section title="Change history" help="Every save is recorded. Restore any earlier version in one click." testId="vb-sec-history"><History items={r.history} onRestore={restore} busy={saving} /></Section>}
        </div>
        <VerticalPreview r={r} tabs={tabs} flows={meta.flows} />
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="rounded-none" data-testid="vb-confirm-dialog">
          <DialogHeader>
            <DialogTitle>This affects {shops} shop{shops !== 1 ? "s" : ""}</DialogTitle>
            <DialogDescription>The change goes out right away to every shop on {orig?.label}. Hidden modules only hide tabs; no data is deleted.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setConfirm(false)} data-testid="vb-confirm-cancel">Cancel</Button>
            <Button className="rounded-none" onClick={save} data-testid="vb-confirm-save">Save for {shops} shop{shops !== 1 ? "s" : ""}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
